<template>
  <div>
    <Head title="Proveedores" />

    <!-- Header & Actions -->
    <div class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <h1 class="text-3xl font-bold text-gray-800">Proveedores</h1>
      
      <div class="flex flex-wrap items-center gap-3">
        <!-- Export Buttons -->
        <a
          target="_blank" href="/providers/export/excel" 
          class="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm"
          title="Exportar a Excel"
        >
          <i class="fa fa-file-excel text-green-600" />
          Excel
        </a>
        <a
          target="_blank" href="/providers/export/pdf" 
          class="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm"
          title="Exportar a PDF"
        >
          <i class="fa fa-file-pdf text-red-600" />
          PDF
        </a>

        <!-- Create Button -->
        <Link
          href="/providers/create" 
          class="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm"
        >
          <i class="fa fa-plus" />
          Nuevo Proveedor
        </Link>
      </div>
    </div>

    <!-- Search & Filters -->
    <div class="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
      <div class="flex flex-col md:flex-row gap-4">
        <div class="flex-1 relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <i class="fa fa-search text-gray-400" />
          </div>
          <input 
            v-model="form.search" 
            type="text" 
            placeholder="Buscar por nombre, empresa, RIF..."
            class="block w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-teal-500 focus:border-teal-500 text-sm transition-colors"
          />
        </div>
        
        <div class="flex items-center gap-4">
          <select 
            v-model="form.trashed" 
            class="block w-full md:w-48 pl-3 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-teal-500 focus:border-teal-500 text-sm transition-colors"
          >
            <option :value="null">Todos los registros</option>
            <option value="with">Incluir eliminados</option>
            <option value="only">Solo eliminados</option>
          </select>
          
          <button 
            class="px-4 py-2.5 text-gray-500 hover:text-teal-600 font-medium text-sm transition-colors" 
            @click="reset"
          >
            Limpiar
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile View (Cards) -->
    <div class="grid grid-cols-1 gap-4 md:hidden mb-6">
      <div
        v-for="provider in providers.data" :key="provider.id" 
        class="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col gap-3"
      >
        <div class="flex justify-between items-start">
          <div class="flex flex-col">
            <Link :href="`/providers/${provider.id}`" class="text-lg font-bold text-teal-700 hover:underline">
              {{ provider.company_name }}
            </Link>
            <span class="text-xs text-gray-500 font-mono">{{ provider.tax_id }}</span>
          </div>
          <div v-if="provider.deleted_at" class="px-2 py-0.5 bg-red-100 text-red-600 text-[10px] font-bold uppercase rounded">
            Eliminado
          </div>
        </div>
        
        <div class="grid grid-cols-2 gap-2 text-sm">
          <div>
            <div class="text-gray-400 text-xs font-bold uppercase tracking-wider">Contacto</div>
            <div class="text-gray-700">{{ provider.name }}</div>
          </div>
          <div>
            <div class="text-gray-400 text-xs font-bold uppercase tracking-wider">Ciudad</div>
            <div class="text-gray-700">{{ provider.city || '-' }}</div>
          </div>
        </div>
        
        <div class="pt-2 border-t border-gray-50 flex items-center justify-between mt-1">
          <div class="flex items-center gap-3">
            <a v-if="provider.email" :href="`mailto:${provider.email}`" class="text-gray-400 hover:text-teal-600 transition-colors">
              <i class="fa fa-envelope" />
            </a>
            <a v-if="provider.phone" :href="`tel:${provider.phone}`" class="text-gray-400 hover:text-teal-600 transition-colors">
              <i class="fa fa-phone" />
            </a>
            <a v-if="provider.website" :href="provider.website" target="_blank" class="text-gray-400 hover:text-teal-600 transition-colors">
              <i class="fa fa-globe" />
            </a>
          </div>
          <Link :href="`/providers/${provider.id}`" class="text-teal-600 font-bold text-sm flex items-center gap-1">
            Ver detalles
            <i class="fa fa-chevron-right text-xs" />
          </Link>
        </div>
      </div>
      <div v-if="providers.data.length === 0" class="bg-white p-12 rounded-xl border border-gray-100 text-center text-gray-400 italic">
        No se encontraron proveedores
      </div>
    </div>

    <!-- Desktop View (Table) -->
    <div class="hidden md:block bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-50/50 border-b border-gray-100">
              <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider">Empresa / RIF</th>
              <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider">Contacto</th>
              <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider">Comunicación</th>
              <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider">Ubicación</th>
              <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="provider in providers.data" :key="provider.id" class="hover:bg-teal-50/30 transition-colors group">
              <td class="py-4 px-6">
                <div class="flex flex-col">
                  <Link :href="`/providers/${provider.id}`" class="font-bold text-gray-900 group-hover:text-teal-700 transition-colors">
                    {{ provider.company_name }}
                    <i v-if="provider.deleted_at" class="fa fa-trash text-red-300 ml-1 text-xs" title="Eliminado" />
                  </Link>
                  <span class="text-xs text-gray-500 font-mono">{{ provider.tax_id }}</span>
                </div>
              </td>
              <td class="py-4 px-6 text-sm text-gray-600">
                {{ provider.name }}
              </td>
              <td class="py-4 px-6">
                <div class="flex items-center gap-3">
                  <a
                    v-if="provider.email" :href="`mailto:${provider.email}`" 
                    class="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-teal-100 hover:text-teal-600 transition-all shadow-sm"
                    title="Enviar correo"
                  >
                    <i class="fa fa-envelope text-xs" />
                  </a>
                  <a
                    v-if="provider.phone" :href="`tel:${provider.phone}`" 
                    class="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-teal-100 hover:text-teal-600 transition-all shadow-sm"
                    title="Llamar"
                  >
                    <i class="fa fa-phone text-xs" />
                  </a>
                  <a
                    v-if="provider.website" :href="provider.website" target="_blank" 
                    class="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-teal-100 hover:text-teal-600 transition-all shadow-sm"
                    title="Sitio Web"
                  >
                    <i class="fa fa-globe text-xs" />
                  </a>
                </div>
              </td>
              <td class="py-4 px-6 text-sm text-gray-600">
                <div v-if="provider.city || provider.country">
                  {{ provider.city }}{{ provider.city && provider.country ? ', ' : '' }}{{ provider.country }}
                </div>
                <div v-else class="text-gray-300 italic text-xs">No especificada</div>
              </td>
              <td class="py-4 px-6 text-right">
                <Link
                  :href="`/providers/${provider.id}`" 
                  class="inline-flex items-center justify-center w-9 h-9 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all"
                >
                  <i class="fa fa-chevron-right" />
                </Link>
              </td>
            </tr>
            <tr v-if="providers.data.length === 0">
              <td colspan="5" class="py-12 text-center text-gray-400 italic">
                No se encontraron proveedores para mostrar
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <pagination 
      v-if="providers.links" 
      :links="providers.links" 
    />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'

export default {
  name: 'ProvidersIndex',

  components: {
    Head,
    Link,
    Pagination,
  },

  layout: Layout,

  props: {
    filters: Object,
    providers: Object,
  },

  data() {
    return {
      form: {
        search: this.filters.search,
        trashed: this.filters.trashed,
      },
    }
  },

  watch: {
    form: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get('/providers', pickBy(this.form), { 
          preserveState: true, 
        })
      }, 300),
    },
  },

  methods: {
    back() {
      this.$inertia.visit('/')
    },

    reset() {
      this.form = mapValues(this.form, () => null)
    },
  },
}
</script>
